import {
  Croissant,
  Coffee,
  Shirt,
  ShoppingBasket,
  Scissors,
  Smartphone,
  Utensils,
  BookOpen,
  Hotel,
  Gem,
  Wrench,
  Pill,
  Plane,
  CalendarDays,
  Sparkles,
  PencilRuler,
  Store,
} from "lucide-react";

// Category-keyword -> fallback stock image (used when a shop has no base64 image)
const FALLBACKS = [
  [["bakery", "cake", "bread"], "https://images.pexels.com/photos/35350970/pexels-photo-35350970.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["cafe", "coffee"], "https://images.pexels.com/photos/16541078/pexels-photo-16541078.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["cloth", "fashion", "boutique", "fancy"], "https://images.pexels.com/photos/5709630/pexels-photo-5709630.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["grocery", "super", "mart"], "https://images.pexels.com/photos/264636/pexels-photo-264636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["salon", "spa", "beauty"], "https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["electronic", "mobile", "gadget"], "https://images.pexels.com/photos/356056/pexels-photo-356056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["restaurant", "hotel", "food", "resort"], "https://images.pexels.com/photos/67468/pexels-photo-67468.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["book", "station"], "https://images.pexels.com/photos/1370295/pexels-photo-1370295.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["jewel", "gold"], "https://images.pexels.com/photos/1191531/pexels-photo-1191531.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["hardware", "workshop", "service", "tool"], "https://images.pexels.com/photos/1249611/pexels-photo-1249611.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["travel", "tour"], "https://images.pexels.com/photos/2087391/pexels-photo-2087391.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
  [["pharma", "medical", "chemist"], "https://images.pexels.com/photos/139398/himalayas-mountains-nepal-himalaya-139398.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"],
];
const DEFAULT_IMG =
  "https://images.pexels.com/photos/1449773/pexels-photo-1449773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export function categoryFallback(category = "") {
  const c = category.toLowerCase();
  for (const [keys, url] of FALLBACKS) {
    if (keys.some((k) => c.includes(k))) return url;
  }
  return DEFAULT_IMG;
}

export function iconForCategory(category = "") {
  const c = category.toLowerCase();
  if (c.includes("bakery")) return Croissant;
  if (c.includes("cafe") || c.includes("coffee")) return Coffee;
  if (c.includes("cloth") || c.includes("fancy") || c.includes("fashion")) return Shirt;
  if (c.includes("grocery")) return ShoppingBasket;
  if (c.includes("salon") || c.includes("spa")) return Scissors;
  if (c.includes("electronic")) return Smartphone;
  if (c.includes("restaurant") || c.includes("food")) return Utensils;
  if (c.includes("hotel") || c.includes("resort")) return Hotel;
  if (c.includes("book") || c.includes("station")) return BookOpen;
  if (c.includes("jewel")) return Gem;
  if (c.includes("hardware") || c.includes("tool")) return Wrench;
  if (c.includes("workshop") || c.includes("service")) return Wrench;
  if (c.includes("pharma") || c.includes("medical")) return Pill;
  if (c.includes("travel") || c.includes("tour")) return Plane;
  if (c.includes("event")) return CalendarDays;
  if (c.includes("stationery")) return PencilRuler;
  return Store;
}

export function toImageSrc(b64) {
  if (!b64) return null;
  if (typeof b64 !== "string") return null;
  if (b64.startsWith("data:") || b64.startsWith("http")) return b64;
  return `data:image/jpeg;base64,${b64}`;
}

export function shopImages(shop) {
  const imgs = [];
  const main = toImageSrc(shop.image_base64);
  if (main) imgs.push(main);
  (shop.gallery_base64s || []).forEach((g) => {
    const s = toImageSrc(g);
    if (s) imgs.push(s);
  });
  if (imgs.length === 0) imgs.push(categoryFallback(shop.category));
  return imgs;
}

// Normalize a backend shop into the shape UI components expect
export function normalizeShop(s) {
  if (!s) return null;
  return {
    id: s.shop_id,
    name: s.name,
    category: s.category,
    description: s.description || "",
    address: [s.address, s.city, s.state].filter(Boolean).join(", "),
    phone: s.phone,
    whatsapp: s.whatsapp,
    lat: s.lat,
    lng: s.lng,
    images: shopImages(s),
    rating: Number(s.rating_avg || 0).toFixed(1),
    review_count: s.rating_count || 0,
    distance_km: typeof s.distance_km === "number" ? Math.round(s.distance_km * 10) / 10 : null,
    approved: s.approval_status === "approved",
    approval_status: s.approval_status,
    verified: s.verified,
    open_time: s.open_time,
    close_time: s.close_time,
    days_open: s.days_open || [],
    business_id: s.business_id,
    owner_id: s.owner_id,
    created_at: s.created_at,
    _raw: s,
  };
}

export function normalizeOffer(o) {
  return {
    id: o.offer_id,
    shop_id: o.shop_id,
    title: o.title,
    description: o.description || "",
    discount:
      o.discount_percent != null && o.discount_percent !== ""
        ? `${o.discount_percent}% OFF`
        : "Special deal",
    image: toImageSrc(o.image_base64) || categoryFallback(o.shop_category || ""),
    shop_name: o.shop_name,
    valid_until: o.valid_until,
    created_at: o.created_at,
  };
}

export function normalizeReview(r) {
  return {
    id: r.review_id || r.id || Math.random().toString(36).slice(2),
    user_name: r.user_name || r.customer_name || r.name || "Guest",
    rating: r.rating || 0,
    comment: r.comment || r.text || "",
    created_at: r.created_at,
  };
}

// Build category objects from the backend's {categories:[strings]}
export function normalizeCategories(list) {
  return (list || []).map((name) => ({
    id: name,
    name,
    icon: iconForCategory(name),
  }));
}

// --- Client-side "nearby shops" ---------------------------------------------
// The backend's /shops/nearby endpoint has been returning [] for real data.
// /shops/search returns the full list with lat/lng, so we compute distance
// client-side. This is what the mobile app effectively does too.

function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * @param {import('axios').AxiosInstance} api
 * @param {{lat:number,lng:number,radius:number,category?:string,q?:string}} opts
 * @returns Promise<Array<ReturnType<typeof normalizeShop>>>
 */
export async function fetchShopsNearby(api, { lat, lng, radius, category, q }) {
  const { data } = await api.get("/shops/search");
  const list = Array.isArray(data) ? data : [];
  const enriched = list
    .filter((s) => typeof s.lat === "number" && typeof s.lng === "number")
    .map((s) => ({
      ...s,
      distance_km: haversineKm(lat, lng, s.lat, s.lng),
    }));

  const qLower = (q || "").trim().toLowerCase();
  const filtered = enriched
    .filter((s) => (radius ? s.distance_km <= radius : true))
    .filter((s) => (category ? (s.category || "").toLowerCase() === category.toLowerCase() : true))
    .filter((s) =>
      qLower
        ? [s.name, s.city, s.address, s.category]
            .filter(Boolean)
            .some((f) => String(f).toLowerCase().includes(qLower))
        : true
    )
    .sort((a, b) => a.distance_km - b.distance_km);

  return filtered.map(normalizeShop);
}
